import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { stringify } from 'qs';
import moment from 'moment';
import { getReservationChangeInfo, menuReselect } from 'api/reservationChangeApi';
import { MenuReselectState } from './MenuReselectPage';

const DAY_OF_WEEK_JA = ['日', '月', '火', '水', '木', '金', '土'];

interface ReservationChangeData {
  reservationNumber: string;
  orgName: string;
  courseName: string;
  desiredTime: string;
  treatmentTime: number;
  customerLastName: string;
  customerFirstName: string;
}

function formatDatetime(isoStr: string, treatmentTime?: number): string {
  const start = moment(isoStr);
  const dow = DAY_OF_WEEK_JA[start.day()];
  const base = `${start.format('YYYY年MM月DD日')}（${dow}）${start.format('HH:mm')}`;
  return treatmentTime ? `${base}（日本時間）〜（約${treatmentTime}分）` : base;
}

const useStyles = makeStyles((theme: Theme) => createStyles({
  pageRoot: {
    minHeight: '100vh',
    backgroundColor: '#fff',
    fontFamily: 'Noto Sans JP',
  },
  contentWrapper: {
    maxWidth: '980px',
    margin: '0 auto',
    padding: '0 20px 40px',
  },
  sectionHeaderRoot: {
    borderBottom: '3px solid #B9BEBE',
    paddingBottom: '25px',
    marginBottom: '20px',
    position: 'relative',
    marginTop: '20px',
    '&::after': {
      content: '""',
      position: 'absolute',
      display: 'block',
      borderBottom: 'solid 3px #cd0021',
      bottom: '-3px',
      width: '100px',
    },
  },
  sectionTitle: {
    fontFamily: 'Noto Sans JP',
    fontWeight: 500,
    fontSize: '30px',
    lineHeight: '45px',
    letterSpacing: '0.8px',
    color: '#000000',
  },
  notiTitle: {
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    marginTop: '20px',
    marginBottom: '20px',
    color: '#333',
  },
  tableSection: {
    width: '100%',
    fontSize: '16px',
    letterSpacing: '0.05em',
    lineHeight: '24px',
    color: '#1a1a1a',
    marginBottom: '2rem',
  },
  tableWrapper: {
    paddingBottom: '1.25em',
    paddingTop: '1.875em',
  },
  tableDl: {
    borderTop: '1px solid #a8a8a8',
    display: 'flex',
    margin: 0,
    padding: 0,
    '&:last-child': { borderBottom: '1px solid #a8a8a8' },
  },
  tableDt: {
    backgroundColor: '#EBEBEB',
    padding: '1em',
    width: '32%',
    textAlign: 'center',
    display: 'table',
    boxSizing: 'border-box',
    fontWeight: 600,
  },
  tableDtTitle: {
    display: 'table-cell',
    verticalAlign: 'middle',
    textAlign: 'center',
  },
  tableDd: {
    width: '68%',
    padding: '1em',
    display: 'table',
    boxSizing: 'border-box',
    marginLeft: '0px',
  },
  tableDdContent: {
    display: 'table-cell',
    verticalAlign: 'middle',
  },
  newValue: {
    color: '#CD0021',
    fontWeight: 600,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '2rem',
  },
  bigBtn: {
    padding: '25px',
    backgroundColor: '#CD0021',
    color: '#ffffff',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '36px',
    textAlign: 'center',
    border: 'none',
    outline: 'none',
    cursor: 'pointer',
    position: 'relative',
    maxWidth: '613px',
    width: '100%',
    '& svg': {
      position: 'absolute',
      top: '50%',
      right: '38px',
      transform: 'translateY(-50%)',
    },
    '&:hover': { opacity: 0.9 },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  backLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
    textAlign: 'center' as const,
    display: 'block',
    marginTop: '1rem',
    fontSize: '0.9rem',
  },
}));

interface LocationState extends MenuReselectState {
  selectedDatetime?: string;
}

function MenuReselectConfirmPage() {
  const classes = useStyles();
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token') || '';
  const companyCode = urlParams.get('companyCode') || '';

  const {
    chosenCourseName = '',
    chosenStoreName = '',
    chosenCourseId = '',
    chosenStoreId = '',
    chosenCourseAcceptRequestReservation = false,
    selectedDatetime = '',
  } = location.state || {};

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<ReservationChangeData | null>(null);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    getReservationChangeInfo(token, companyCode)
      .then((res) => { setData(res.data || null); })
      .catch(() => { setData(null); })
      .finally(() => { setLoading(false); });
  }, [token, companyCode]);

  const handleConfirm = () => {
    if (!selectedDatetime || !chosenCourseId || !chosenStoreId) return;
    setSubmitting(true);
    menuReselect(token, companyCode, {
      newContractCourseId: chosenCourseId,
      newOrgId: chosenStoreId,
      newDesiredTime: selectedDatetime,
    })
      .then(() => {
        history.push(
          `/contact/reservationChangeComplete?${stringify({ token, companyCode })}`,
          { acceptRequestReservation: chosenCourseAcceptRequestReservation },
        );
      })
      .catch((err: any) => {
        const code = err?.response?.data?.errors?.[0]?.message || '';
        if (code === 'CHANGE_DEADLINE_EXCEEDED') {
          enqueueSnackbar('変更期限を過ぎているため変更できません。', { variant: 'error' });
        } else {
          enqueueSnackbar('予約変更に失敗しました。時間をおいて再度お試しください。', { variant: 'error' });
        }
      })
      .finally(() => { setSubmitting(false); });
  };

  const handleBack = () => {
    history.replace(
      `/contact/reservationMenuReselectDate?${stringify({ token, companyCode })}`,
      location.state,
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={4}>
        <Typography variant="h6">データを取得できませんでした。</Typography>
      </Box>
    );
  }

  const beforeDatetime = formatDatetime(data.desiredTime, data.treatmentTime);
  const newDatetime = selectedDatetime ? formatDatetime(selectedDatetime) : '（未選択）';

  const rows: { title: string; content: string; highlight?: boolean }[] = [];

  // サロン
  if (data.orgName !== chosenStoreName) {
    rows.push({ title: '変更前のサロン', content: data.orgName });
    rows.push({ title: '変更後のサロン', content: chosenStoreName, highlight: true });
  } else {
    rows.push({ title: 'サロン', content: data.orgName });
  }

  // コース
  if (data.courseName !== chosenCourseName) {
    rows.push({ title: '変更前のコース', content: data.courseName });
    rows.push({ title: '変更後のコース', content: chosenCourseName, highlight: true });
  } else {
    rows.push({ title: 'コース', content: chosenCourseName });
  }

  // 予約日時
  if (beforeDatetime !== newDatetime) {
    rows.push({ title: '変更前の予約日時', content: beforeDatetime });
    rows.push({ title: '変更後の予約日時', content: newDatetime, highlight: true });
  } else {
    rows.push({ title: '予約日時', content: beforeDatetime });
  }

  // 予約番号・お名前（変更なし）
  rows.push({ title: '予約番号', content: data.reservationNumber });
  rows.push({
    title: 'お名前',
    content: `${data.customerLastName} ${data.customerFirstName}`,
  });

  return (
    <div className={classes.pageRoot}>
      <div className={classes.contentWrapper}>
        <div className={classes.sectionHeaderRoot}>
          <Typography className={classes.sectionTitle}>
            {chosenCourseAcceptRequestReservation ? '仮予約内容確認' : 'ご予約内容確認'}
          </Typography>
        </div>

        <Typography className={classes.notiTitle}>
          {chosenCourseAcceptRequestReservation ? (
            <>
              選択されたコースは仮予約となっております。
              <br />
              店舗よりご予約確認のお電話を差し上げます。
              <br />
              お電話が繋がらない場合はキャンセルとなりますので予めご了承ください。
              <br />
              お電話は 0800 で始まる電話番号よりご連絡いたします。
            </>
          ) : (
            '変更内容をご確認ください。修正する場合は「戻る」を押してください。'
          )}
        </Typography>

        <div className={classes.tableSection}>
          <div className={classes.tableWrapper}>
            {rows.map((row) => (
              <dl key={row.title} className={classes.tableDl}>
                <dt className={classes.tableDt}>
                  <span className={classes.tableDtTitle}>{row.title}</span>
                </dt>
                <dd className={classes.tableDd}>
                  <p className={`${classes.tableDdContent} ${row.highlight ? classes.newValue : ''}`}>
                    {row.content}
                  </p>
                </dd>
              </dl>
            ))}
          </div>
        </div>

        <div className={classes.buttonContainer}>
          <button type="button" className={classes.bigBtn} onClick={handleConfirm} disabled={submitting}>
            <span>お申し込みを確定</span>
            <svg width="23" height="20" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.932 9.952L1.66 0.711999L0.82 1.888L12.412 9.952V10.048L0.82 18.112L1.66 19.288L14.932 10.048V9.952ZM22.18 9.952L8.908 0.711999L8.092 1.888L19.66 9.952V10.048L8.092 18.112L8.908 19.288L22.18 10.048V9.952Z" fill="white" />
            </svg>
          </button>
          <Typography
            component="span"
            className={classes.backLink}
            onClick={handleBack}
          >
            戻る
          </Typography>
        </div>
      </div>
    </div>
  );
}

export default MenuReselectConfirmPage;
